import { Component, Inject, Input, NgZone, PLATFORM_ID, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';
import { ReportService } from 'src/app/reports/services/report.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/common/services/shared.service';

@Component({
  selector: 'app-waterfall-chart',
  templateUrl: './waterfall-chart.component.html',
  styleUrls: ['./waterfall-chart.component.scss']
})
export class WaterfallChartComponent {
  lang!: number;
  private root: am5.Root | undefined;
  @Input() chartId='abc';
  @Input() data: any = [];
  @Input() label: any = '';
  @Input() showSideValue: boolean=true;
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private zone: NgZone,
    private reportService: ReportService,
    private translate: TranslateService,
    private sharedService: SharedService
  ) { }
  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          f();
        }, 500)
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.lang = this.sharedService.getCurrentLangID();
    this.browserOnly(() => {
      if (this.root && !this.root.isDisposed()) {
        this.root.dispose();
      }

      this.processWaterfallData();
      this.createChart();
    });
  }

  ngAfterViewInit() {
  this.browserOnly(() => {
    // this.createChart();
  });
}

  /**
   * 
   * @param data 
   * Processor function to transform data to waterfall format
   */
  processWaterfallData() {
    const result = [];

    for (let i = 0; i < this.data.length; i++) {
      const current = this.data[i].rate;
      const previous = i === 0 ? 0 : this.data[i - 1].rate;
      const difference = current - previous;

      let prefix = '';
      if (difference > 0) prefix = '+';
      else if (difference < 0) prefix = '-';

      result.push({
        category: this.data[i].year,
        open: previous,  // Final bar starts from 0 else prev value
        value: current,
        stepValue: i === this.data.length - 1 ? undefined : current, // Make Step dottoed line
        displayValue: `${i === 0 ? '' : prefix}${this.reportService.formatInternationNum(Math.abs(difference))}`, //compare prev value inrease above 
        columnConfig: {
          fill: am5.color(difference > 0 ? "#2372F0" : "#FF6C16") // if positive color blue else color orange
        }
      });
    }
    return result;
  }

  createChart() {
    this.browserOnly(() => {
      if (this.root && !this.root.isDisposed()) {
        this.root.dispose(); // ✅ clean old chart
      }

      let root = am5.Root.new(`${this.chartId}`);

      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      var chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        paddingLeft: 0,
        layout: root.verticalLayout
      }));

      // Create axes
      // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      var xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 30,
        minorGridEnabled: true
      });
      // Add this configuration for the x-axis labels
      xRenderer.labels.template.setAll({
        fill: am5.color("#6C737F"), // Set your desired font color (e.g., black)
        fontWeight: "500",        // Set font weight to bold
        fontSize: 10,               // Optional: adjust font size if needed
        fontFamily: 'Manrope'
      });

      chart.set("paddingTop", 0); // Add this line before the label
      chart.set("paddingLeft", 15); // Add this line before the label
      chart.set("paddingRight", 20); // Add this line before the label
      
      if(this.showSideValue){
        // Add vertical label to the left side
        var verticalLabel = chart.children.unshift(am5.Label.new(root, {
          text: this.translate.instant('reports.area'),
          rotation: 0,
          x: 0,
          y: am5.percent(0),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          fontSize: 10,
          fontWeight: "400",
          fill: am5.color("#000000"),
          fontFamily: 'Manrope',
          dx: 20,
          dy: 20
        }));
        // Add bottom-right label
      
        var bottomRightLabel = chart.children.push(am5.Label.new(root, {
          text: this.translate.instant('year'), // Your custom label
          x: am5.percent(100), // Align to right
          y: am5.percent(100), // Align to bottom
          centerX: am5.percent(100), // Anchor to right
          centerY: am5.percent(100), // Anchor to bottom
          dy: -25, // Move slightly upward (to not touch the edge)
          dx: 22, // Move slightly left (padding from right edge)
          fontSize: 10,
          fontWeight: "400",
          fill: am5.color("#000000"),
          fontFamily: 'Manrope',
          position: "absolute" //
        }));
      }

      // After creating the chart, add a label at the bottom
      var gradeLabel = chart.children.push(am5.Label.new(root, {
        text: this.label,
        x: am5.percent(50),  // Center horizontally
        y: am5.percent(100), // Place at the bottom
        centerX: am5.percent(50), // Ensure proper centering
        centerY: am5.p100,       // Anchor to bottom
        dy: 15,                  // Adjust vertical position (increase to move further down)
        fontSize: 16,
        fill: am5.color("#000000"), // Gray color
        fontWeight: "600",
        fontFamily: 'Manrope',
        direction: this.lang === 2 ? 'rtl' : 'ltr'
      }));

      var xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: "category",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));

      xRenderer.grid.template.setAll({
        location: 1,
        visible: false // Remove vertical grid lines
      })

      var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        // maxDeviation: 0,
        extraMax: 0.2,
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 0.05,
        }),
        tooltip: am5.Tooltip.new(root, {})
      }));

      // Hide Y-axis labels but keep grid lines
      yAxis.get("renderer").labels.template.setAll({
        visible: false
      });


      // To ensure grid lines remain visible
      yAxis.get("renderer").grid.template.setAll({
        strokeOpacity: 0.05,
        visible: true
      });

      // Create series
      var series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: this.label,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        openValueYField: "open",
        categoryXField: "category",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{categoryX}: {valueY.formatNumber('#,##,###.#')}",
          forceHidden: false, // Ensure tooltip is interactive
          autoTextColor: false, // Disable automatic text color adjustment
        })
      }));

      // Enable hover effects on columns
      series.columns.template.setAll({
        cursorOverStyle: "pointer", // Show pointer cursor on hover
        tooltipText: "{categoryX}: {valueY.formatNumber('#,##,###.#')}", // Tooltip content
        strokeOpacity: 0, // No border
        cornerRadiusTL: 8, // Top-left rounded
        cornerRadiusTR: 8, // Top-right rounded
        cornerRadiusBL: 0, // Bottom-left sharp
        cornerRadiusBR: 0,  // Bottom-right sharp
      });

      series.columns.template.adapters.add("height", function (height: any, target: any) {
        const dataItem = target.dataItem;
        if (!dataItem) return height;

        const value = dataItem.get("valueY");
        const openValue = dataItem.get("openValueY");
        const difference = Math.abs(value - openValue);

        if (difference == 0) {
          return 1; // ensure at least 2px height for visibility
        }
        if (difference < 100) {
          return 4; // ensure at least 5px height for visibility
        }

        return height;
      });


      // Ensure tooltips are interactive
      series.set("interactive", true);

      series.columns.template.setAll({
        templateField: "columnConfig",
        strokeOpacity: 0,
      })

      //Series Above number
      series.bullets.push(function (root, series, dataItem: any) {
        const valueY: any = dataItem.get("valueY");
        const openValueY: any = dataItem.get("openValueY");
        const displayValue = dataItem.dataContext.displayValue;


        const isPositive = valueY >= openValueY;

        // If the difference is 0, skip creating the label bullet

        // if (displayValue == '') {

        //   return undefined; // skip creating the bullet
        // }
        return am5.Bullet.new(root, {
          locationY: isPositive ? 1 : 0, // Position at the top of the bar
          sprite: am5.Label.new(root, {
            text: displayValue !== '' ? String(displayValue) : "0",
            centerX: am5.p50, // horizontally center the text
            centerY: am5.p100, // vertically anchor the label at the bottom
            dy: 0, // move it up slightly above the bar
            populateText: true,
            fontSize: 10,
            fontWeight: '700',
            fill: am5.color(isPositive ? '#2372F0' : '#FF6C16') // blue text
          })
        });
      });



      var stepSeries = chart.series.push(am5xy.StepLineSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "stepValue",
        categoryXField: "category",
        noRisers: true,
        locationX: 0.65,
        stroke: root.interfaceColors.get("alternativeBackground")
      }));

      stepSeries.strokes.template.setAll({
        strokeDasharray: [3, 3]
      })

      var colorSet = am5.ColorSet.new(root, {});

      // Set data
      var data = this.processWaterfallData();
      xAxis.data.setAll(data);
      series.data.setAll(data);
      stepSeries.data.setAll(data);

      // Make stuff animate on load
      chart.appear(1000, 100);
      this.root = root;
    });
  }

  ngOnDestroy() {
   this.browserOnly(() => {
    if (this.root && !this.root.isDisposed()) {
      this.root.dispose();
    }
  });
  }
}
